import { getPositionBadgeStyles, getPositionText } from '@/shows/utils/formatters';
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Grid,
} from "@mui/material";
import {
  Event as EventIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  Timer as TimerIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useAppContext, useHoldsApi } from "@/context/AppContext";
import type { Hold } from "api-client/models";
import { Breadcrumbs } from "@/utils/components";
import { BookingPrivateNotes } from "./BookingPrivateNotes";
import BookingWorkflowPanel from "./BookingWorkflowPanel";
import AdminJson from "@/utilities/AdminJson";
import { apiDateToDateOnly, formatDateLong, formatDateLocal } from "@/utils/dateUtils";

// Type for raw API response (snake_case)
interface HoldApiResponse {
  id: string;
  stage: string;
  stage_name: string;
  stage_venue_name: string;
  hold_date: string;
  promoter_team: number;
  promoter_team_name: string;
  show: string | null;
  show_title: string | null;
  hold_position: number;
  status: string;
  date_status: string;
  challenged_at: string | null;
  challenger_team: number | null;
  challenger_team_name: string | null;
  challenge_response_deadline: string | null;
  expires_at: string | null;
  notes: string;
  is_expired: boolean;
  time_until_expiration?: string | null;
  time_until_challenge_deadline?: string | null;
  created_at: string;
  updated_at?: string;
  is_active: boolean;
}

const HoldDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDevMode, team } = useAppContext();
  const holdsApi = useHoldsApi();

  const [hold, setHold] = useState<Hold | null>(null);
  const [allHoldsOnDate, setAllHoldsOnDate] = useState<Hold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const bookingSaved = (saved: Hold): void => {
    setHold(saved);
    setRefreshVersion(version => version + 1);
  };

  // Load hold details and all holds on same date/stage
  useEffect(() => {
    if (!id) {
      setError("Hold ID is required");
      setLoading(false);
      return;
    }

    let current = true;
    const loadHoldData = async () => {
      try {
        const holdData = await holdsApi.holdsRetrieve({ id });
        if (!current) return;
        setHold(holdData);

        // Fetch all holds on the same date and stage using calendar endpoint
        if (holdData.holdDate && holdData.stage) {
          // Format date as YYYY-MM-DD for query parameter
          const dateStr = apiDateToDateOnly(holdData.holdDate);

          // Use the calendar endpoint with date range (single day)
          const response = await fetch(
            `/api/bookings/holds/calendar/?start_date=${dateStr}&end_date=${dateStr}&stage=${holdData.stage}`,
            {
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                'X-Team-ID': String(team?.id ?? ''),
              },
            },
          );

          if (response.ok) {
            const data = (await response.json()) as HoldApiResponse[];
            if (!current) return;

            // Transform snake_case API response to camelCase for TypeScript
            const transformedHolds: Hold[] = data.map((h: HoldApiResponse) => {
              return {
                id: h.id,
                stage: h.stage,
                stageName: h.stage_name,
                stageVenueName: h.stage_venue_name,
                holdDate: new Date(h.hold_date),
                promoterTeam: h.promoter_team,
                promoterTeamName: h.promoter_team_name,
                show: h.show,
                showTitle: h.show_title,
                holdPosition: h.hold_position,
                status: h.status,
                dateStatus: h.date_status,
                challengedAt: h.challenged_at
                  ? new Date(h.challenged_at)
                  : undefined,
                challengerTeam: h.challenger_team || undefined,
                challengerTeamName: h.challenger_team_name || undefined,
                challengeResponseDeadline: h.challenge_response_deadline
                  ? new Date(h.challenge_response_deadline)
                  : undefined,
                expiresAt: h.expires_at ? new Date(h.expires_at) : undefined,
                notes: h.notes || "",
                isExpired: String(h.is_expired),
                timeUntilExpiration: h.time_until_expiration || undefined,
                timeUntilChallengeDeadline:
                  h.time_until_challenge_deadline || undefined,
                createdAt: new Date(h.created_at),
                updatedAt: h.updated_at ? new Date(h.updated_at) : undefined,
                isActive: h.is_active,
              } as unknown as Hold;
            });

            // Sort by position
            const sortedHolds = transformedHolds.sort(
              (a, b) => (a.holdPosition || 0) - (b.holdPosition || 0),
            );
            setAllHoldsOnDate(sortedHolds);
          }
        }
      } catch {
        if (current) setError("Failed to load hold details");
      } finally {
        if (current) setLoading(false);
      }
    };

    loadHoldData();
    return () => { current = false; };
  }, [id, holdsApi, team?.id, refreshVersion]);

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "pending":
        return "primary";
      case "challenged":
        return "warning";
      case "confirmed":
        return "success";
      case "released":
        return "default";
      case "expired":
        return "error";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };


  const formatTimeRemaining = (timeString: string) => {
    if (!timeString || timeString === "N/A") return "N/A";
    return timeString;
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 0, sm: 3 }, overflowWrap: 'anywhere' }}>
        <LinearProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading hold details...
        </Typography>
      </Box>
    );
  }

  if (error || !hold) {
    return (
      <Box sx={{ p: { xs: 0, sm: 3 }, overflowWrap: 'anywhere' }}>
        <Alert severity="error">{error || "Hold not found"}</Alert>
        <Button
          variant="outlined"
          onClick={() => navigate("/shows/calendar")}
          sx={{ mt: 2 }}
        >
          Back to Calendar
        </Button>
      </Box>
    );
  }

  const isOwnHold = team && hold.promoterTeam === team.id;
  const canEdit = isOwnHold && hold.canManage && hold.status !== "requested";

  const breadcrumbItems = [
    { label: "Shows", path: "/shows" },
    { label: "Calendar", path: "/shows/calendar" },
    // Add show link if hold is associated with a show
    ...(hold.show && hold.showTitle && hold.showTeam === team?.id
      ? [{ label: hold.showTitle, path: `/shows/${hold.show}` }]
      : []),
    { label: "Hold Details" },
  ];

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, overflowWrap: 'anywhere' }}>
      <Breadcrumbs items={breadcrumbItems} />
      <BookingWorkflowPanel hold={hold} onSaved={bookingSaved} />
      <BookingPrivateNotes key={`${hold.id}-${hold.bookingRevision}`} hold={hold} onSaved={bookingSaved} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2, mb: 3 }}
      >
        <Typography variant="h4">Hold Details</Typography>

        {canEdit && (
          <Tooltip title="Edit Hold">
            <IconButton
              color="primary"
              onClick={() => navigate(`/bookings/${hold.id}/edit`)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Main Hold Information */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mb: 2 }}
              >
                {hold.show ? (
                  <Typography
                    variant="h5"
                    onClick={() => { if (hold.showTeam === team?.id) navigate(`/shows/${hold.show}`); }}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "readable.primary",
                      },
                      transition: "color 0.2s",
                    }}
                  >
                    {hold.showTitle || "Untitled Hold"}
                  </Typography>
                ) : (
                  <Typography variant="h5">
                    {hold.showTitle || "Untitled Hold"}
                  </Typography>
                )}

                <Chip
                  label={hold.holdPosition ? `${getPositionText(hold.holdPosition)} Position` : "No reservation"}
                  sx={{
                    ...getPositionBadgeStyles(hold.holdPosition),
                    fontWeight: "bold",
                  }}
                />
              </Box>

              <Stack spacing={2}>
                {/* Status */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={hold.status?.toUpperCase() || "UNKNOWN"}
                    color={getStatusColor(hold.status)}
                    size="small"
                  />
                </Box>

                {/* Date */}
                <Box display="flex" alignItems="center" gap={1}>
                  <EventIcon color="action" />
                  <Typography variant="body1">
                    {formatDateLong(hold.holdDate)}
                  </Typography>
                </Box>

                {/* Venue & Stage */}
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationIcon color="action" />
                  <Typography variant="body1">
                    {hold.stageVenueName} - {hold.stageName}
                  </Typography>
                </Box>

                {/* Promoter */}
                <Box display="flex" alignItems="center" gap={1}>
                  <GroupIcon color="action" />
                  <Typography variant="body1">
                    {hold.promoterTeamName}
                    {isOwnHold && (
                      <Chip label="Your Hold" size="small" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                </Box>

                {/* Challenge Information */}
                {hold.status === "challenged" && (
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <Typography variant="body2">
                      <strong>This hold is being challenged!</strong>
                      {hold.challengerTeamName && (
                        <> Challenger: {hold.challengerTeamName}</>
                      )}
                      {hold.challengeResponseDeadline && (
                        <>
                          <br />
                          Response deadline:{" "}
                          {formatDateLocal(hold.challengeResponseDeadline)}
                        </>
                      )}
                    </Typography>
                  </Alert>
                )}

                {/* Notes */}
                {hold.notes && (
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Notes:
                    </Typography>
                    <Typography variant="body1">{hold.notes}</Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>


          </Card>

          {/* Confirmed Hold Notice - Direct users to show page for operational data */}
          {hold.status === "confirmed" && (
            <Alert severity="success" sx={{ mt: 3 }}>
              <AlertTitle>Booking confirmed</AlertTitle>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Manage shared terms and settlement in the shared booking panel.
                Each business keeps its internal notes and accounting private.
              </Typography>
              {hold.show && hold.showTeam === team?.id && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EventIcon />}
                  onClick={() => { if (hold.showTeam === team?.id) navigate(`/shows/${hold.show}`); }}
                  sx={{ mt: 1 }}
                >
                  Go to Show Page
                </Button>
              )}
            </Alert>
          )}
        </Grid>

        {/* Timing Information */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TimerIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Timing Information
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Hold Created:
                  </Typography>
                  <Typography variant="body1">
                    {formatDateLocal(hold.createdAt)}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Expires At:
                  </Typography>
                  <Typography variant="body1">
                    {hold.expiresAt ? formatDateLocal(hold.expiresAt) : "N/A"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Time Until Expiration:
                  </Typography>
                  <Typography
                    variant="body1"
                    color={hold.isExpired ? "error" : "text.primary"}
                  >
                    {formatTimeRemaining(hold.timeUntilExpiration)}
                  </Typography>
                </Box>

                {hold.status === "challenged" && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Challenge Response Time:
                      </Typography>
                      <Typography variant="body1" color="statusText.inFlight">
                        {formatTimeRemaining(hold.timeUntilChallengeDeadline)}
                      </Typography>
                    </Box>
                  </>
                )}

                {hold.isExpired && (
                  <Alert severity="error" icon={<TimerIcon />}>
                    This hold has expired and will be automatically released.
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Calendar Day View - All Holds on This Date */}
      {allHoldsOnDate.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <EventIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              All Holds on {formatDateLong(hold.holdDate)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {hold.stageVenueName} - {hold.stageName}
            </Typography>

            <Stack spacing={1}>
              {allHoldsOnDate.map((h) => {
                const isCurrentHold = h.id === hold.id;
                const isOwnHold = team && h.promoterTeam === team.id;

                return (
                  <Card
                    key={h.id}
                    variant="outlined"
                    sx={{
                      backgroundColor: isCurrentHold
                        ? "action.selected"
                        : "background.paper",
                      border: isCurrentHold ? 2 : 1,
                      borderColor: isCurrentHold ? "readable.link" : "divider",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "readable.link",
                        transform: "translateX(4px)",
                      },
                    }}
                    onClick={() => {
                      // Always prefer show navigation when the row has an associated show,
                      // including the currently viewed hold row.
                      if (h.show) {
                        navigate(`/shows/${h.show}`);
                        return;
                      }

                      // Fallback to hold details only for other rows without an associated show.
                      if (h.id !== hold.id) {
                        navigate(`/bookings/${h.id}`);
                      }
                    }}
                  >
                    <CardContent
                      sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <Chip
                            label={getPositionText(h.holdPosition)}
                            sx={{
                              ...getPositionBadgeStyles(h.holdPosition),
                              fontWeight: "bold",
                              minWidth: 50,
                            }}
                            size="small"
                          />

                          <Box>
                            <Typography
                              variant="body1"
                              fontWeight={isCurrentHold ? "bold" : "normal"}
                            >
                              {h.showTitle || "Untitled Hold"}
                              {isCurrentHold && (
                                <Chip
                                  label="Current"
                                  size="small"
                                  color="primary"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {h.promoterTeamName}
                              {isOwnHold && " (Your Hold)"}
                            </Typography>
                          </Box>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={h.status?.toUpperCase() || "UNKNOWN"}
                            color={getStatusColor(h.status)}
                            size="small"
                          />

                          {h.status === "challenged" && (
                            <Tooltip
                              title={`Challenged by ${h.challengerTeamName || "Unknown"}`}
                            >
                              <WarningIcon color="warning" />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>

            {allHoldsOnDate.length > 1 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {allHoldsOnDate.length} holds competing for this date.
                  {hold.holdPosition > 1 &&
                    ` You are in ${getPositionText(hold.holdPosition)} position.`}
                  {hold.holdPosition === 1 && " You have first priority!"}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Operational Data Section - Only show if operational data exists AND hold is not confirmed */}
      {hold.status !== "confirmed" &&
        (hold.doorsTime ||
          hold.showTime ||
          hold.endTime ||
          hold.attendance !== undefined ||
          hold.showCapacity !== undefined ||
          hold.ticketRevenue ||
          hold.barRevenue ||
          hold.otherRevenue ||
          hold.totalExpenses ||
          hold.isSettled) && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Show Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Operational details for this specific date
              </Typography>

              {/* Show Timing */}
              {(hold.doorsTime || hold.showTime || hold.endTime) && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Show Timing
                  </Typography>
                  <Grid container spacing={2}>
                    {hold.doorsTime && (
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Doors Open
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {hold.doorsTime}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {hold.showTime && (
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Show Starts
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {hold.showTime}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {hold.endTime && (
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Show Ends
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {hold.endTime}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Attendance & Capacity */}
              {(hold.attendance !== undefined ||
                hold.showCapacity !== undefined) && (
                <Box sx={{ mb: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Attendance
                  </Typography>
                  <Grid container spacing={2}>
                    {hold.showCapacity !== undefined && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Show Capacity
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {hold.showCapacity.toLocaleString()} people
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {hold.attendance !== undefined && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Actual Attendance
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {hold.attendance.toLocaleString()} people
                            {hold.showCapacity && (
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                (
                                {Math.round(
                                  (hold.attendance / hold.showCapacity) * 100,
                                )}
                                % capacity)
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Revenue & Financial Data */}
              {(hold.ticketRevenue ||
                hold.barRevenue ||
                hold.otherRevenue ||
                hold.totalExpenses) && (
                <Box sx={{ mb: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Financial Summary
                  </Typography>
                  <Grid container spacing={2}>
                    {hold.ticketRevenue && (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Ticket Revenue
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="medium"
                            color="statusText.ready"
                          >
                            $
                            {parseFloat(hold.ticketRevenue).toLocaleString(
                              "en-US",
                              { minimumFractionDigits: 2 },
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {hold.barRevenue && (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Bar Revenue
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="medium"
                            color="statusText.ready"
                          >
                            $
                            {parseFloat(hold.barRevenue).toLocaleString(
                              "en-US",
                              { minimumFractionDigits: 2 },
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {hold.otherRevenue && (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Other Revenue
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="medium"
                            color="statusText.ready"
                          >
                            $
                            {parseFloat(hold.otherRevenue).toLocaleString(
                              "en-US",
                              { minimumFractionDigits: 2 },
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {hold.totalExpenses && (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Total Expenses
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="medium"
                            color="statusText.attention"
                          >
                            $
                            {parseFloat(hold.totalExpenses).toLocaleString(
                              "en-US",
                              { minimumFractionDigits: 2 },
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Calculate and show totals */}
                  {(hold.ticketRevenue ||
                    hold.barRevenue ||
                    hold.otherRevenue) && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Revenue
                          </Typography>
                          <Typography variant="h6" color="statusText.ready">
                            $
                            {(
                              (hold.ticketRevenue
                                ? parseFloat(hold.ticketRevenue)
                                : 0) +
                              (hold.barRevenue
                                ? parseFloat(hold.barRevenue)
                                : 0) +
                              (hold.otherRevenue
                                ? parseFloat(hold.otherRevenue)
                                : 0)
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </Typography>
                        </Grid>
                        {hold.totalExpenses && (
                          <>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Total Expenses
                              </Typography>
                              <Typography variant="h6" color="statusText.attention">
                                $
                                {parseFloat(hold.totalExpenses).toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 2,
                                  },
                                )}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Net Profit
                              </Typography>
                              <Typography
                                variant="h6"
                                color={
                                  (hold.ticketRevenue
                                    ? parseFloat(hold.ticketRevenue)
                                    : 0) +
                                    (hold.barRevenue
                                      ? parseFloat(hold.barRevenue)
                                      : 0) +
                                    (hold.otherRevenue
                                      ? parseFloat(hold.otherRevenue)
                                      : 0) -
                                    parseFloat(hold.totalExpenses) >=
                                  0
                                    ? "statusText.ready"
                                    : "statusText.attention"
                                }
                              >
                                $
                                {(
                                  (hold.ticketRevenue
                                    ? parseFloat(hold.ticketRevenue)
                                    : 0) +
                                  (hold.barRevenue
                                    ? parseFloat(hold.barRevenue)
                                    : 0) +
                                  (hold.otherRevenue
                                    ? parseFloat(hold.otherRevenue)
                                    : 0) -
                                  parseFloat(hold.totalExpenses)
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                })}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Box>
              )}

              {/* Settlement Information */}
              {hold.isSettled && (
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Settlement Status
                  </Typography>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>This show has been settled</strong>
                      {hold.settlementDate && (
                        <> on {formatDateLong(new Date(hold.settlementDate))}</>
                      )}
                    </Typography>
                  </Alert>
                  {hold.settlementNotes && (
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Settlement Notes:
                      </Typography>
                      <Typography variant="body1">
                        {hold.settlementNotes}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

      <AdminJson isDebug={isDevMode} data={{ hold, allHoldsOnDate }} />

    </Box>
  );
};

export default HoldDetails;
